import { Component, OnInit, ViewChild } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { cloneDeep } from 'lodash';
import { Subject, catchError, filter, forkJoin, of, switchMap, tap } from 'rxjs';
import { FORMLY_FIELD_TYPES } from '@app/constants';
import { DialogService, FeedbackService } from '@app/core/services';
import { FilterConfig, TableConfig } from '@app/interfaces';
import { TableComponent, TitleButton, TitleButtonsComponent, TitleComponent } from '@app/shared/components';
import { BaseFilterComponent } from '@app/shared/components/base-filter.component';
import { FiltersComponent } from '@app/shared/forms/components/filters/filters.component';
import { GlobalServerService, GlobalUnitService } from '@app/shared/services';
import { AlternateServer } from './models/alternate-server-response.model';

@Component({
	selector: 'sb-alternate-host-server-setup',
	standalone: true,
	imports: [TableComponent, TitleComponent, TitleButtonsComponent, MatCardModule, FiltersComponent],
	templateUrl: './alternate-host-server-setup.component.html',
	styleUrl: './alternate-host-server-setup.component.less',
})
export class AlternateHostServerSetupComponent extends BaseFilterComponent<AlternateServer> implements OnInit {
	@ViewChild(TableComponent) tbl: TableComponent;

	constructor(
		private readonly dialogService: DialogService,
		private readonly globalServerService: GlobalServerService,
		private readonly globalUnitService: GlobalUnitService,
		private readonly feedbackService: FeedbackService,
		private readonly dialog: MatDialog
	) {
		super();
	}

	form = new UntypedFormGroup({});
	options: FormlyFormOptions = {
		formState: {
			mode: 'edit',
			selects: {},
		},
	};

	homeDbServerName: string;
	filterOptions: FormlyFormOptions = {};
	filterConfig: FilterConfig<any>[] = [];
	filterFields: FormlyFieldConfig[] = [];
	filters = { search: '' };

	searchOnly = true;

	destroy$ = new Subject();

	model = {
		ServerName: '',
		Description: '',
		DisplayName: '',
		Id: null,
		Password: null,
		UserID: null,
	};

	templateModel = {
		ServerName: '',
		Description: '',
		DisplayName: '',
		Id: null,
		Password: null,
		UserID: null,
	};

	fields: FormlyFieldConfig[] = [];
	data = [];
	titleButtons: TitleButton[] = [
		{
			label: 'Add Server',
			onClick: () => {
				this.model = cloneDeep(this.templateModel);
				this.add(this.model);
			},
			icon: 'add',
		},
	];

	tableConfig: TableConfig = {
		defaultSortDirection: 'asc',
		defaultSortField: 'ServerName',
		columns: [
			{
				key: 'ServerName',
				header: 'Server Name',
			},
			{
				key: 'Description',
				header: 'Description',
			},
		],
		showRowActions: true,
		actions: [
			{
				label: 'Delete',
				onClick: (row) => {
					this.globalServerService.deleteItem(row.Id).subscribe(() => {
						this.loadData();
						this.feedbackService.success('deleted', 'Alternate Server');
					});
				},
			},
			{
				label: 'Edit',
				onClick: (row) => {
					this.showDetails(row);
				},
			},
		],
		onRowClick: (row) => {
			this.showDetails(row);
		},
	};

	ngOnInit() {
		this.loadData();
	}

	showDetails(row: any) {
		this.globalServerService
			.getItem(row.Id)
			.pipe(
				filter((response) => !!response),
				tap((response) => {
					this.model = cloneDeep(this.templateModel);
					this.model = { ...this.model, ...response.item };
				}),
				switchMap(() =>
					this.dialogService.open({
						title: this.model.ServerName,
						form: this.form,
						fields: this.fields,
						model: this.model,
						options: this.options,
						customButtons: [
							{
								action: () => {
									this.globalServerService.testConnection(this.model).subscribe((result: any) => {
										this.displayConnectionStatus(result);
									});
								},
								text: 'Test Connection',
							},
						],
					})
				),
				filter((result) => !!result),
				switchMap(() =>
					this.globalServerService.saveItem(this.model).pipe(
						tap(() => this.loadData()),
						catchError((error) => {
							this.dialogService.open({
								title: 'Error Saving',
								message: error.error?.message,
							});
							return of(null);
						})
					)
				)
			)
			.subscribe((response) => {
				if (response) {
					this.feedbackService.success('updated', 'Alternate Server');
				}
			});
	}

	add(model: any) {
		this.dialogService
			.open({
				title: 'Add Server',
				form: this.form,
				fields: this.fields,
				model,
				options: this.options,
				customButtons: [
					{
						action: () => {
							this.globalServerService.testConnection(this.model).subscribe((result: any) => {
								this.displayConnectionStatus(result);
							});
						},
						text: 'Test Connection',
					},
				],
			})
			.pipe(
				filter((result) => !!result),
				switchMap(() =>
					this.globalServerService.addItem(this.model).pipe(
						tap(() => this.loadData()),
						catchError((error) => {
							this.dialogService.open({
								title: 'Error Saving',
								message: error.error?.message,
							});
							return of(null);
						})
					)
				)
			)
			.subscribe((response) => {
				if (response) {
					this.feedbackService.success('created', 'Alternate Server');
				}
			});
	}

	loadData() {
		forkJoin([this.globalUnitService.getHomeDbServerName(), this.globalServerService.getList()]).subscribe(([homeDbServerName, serverList]) => {
			this.homeDbServerName = homeDbServerName.serverName;
			this.data = serverList.items;
			this.fields = this.loadFields();
		});
	}

	loadFields() {
		return [
			{
				key: 'ServerName',
				type: FORMLY_FIELD_TYPES.input,
				props: {
					label: 'Server Name',
					required: true,
					maxLength: 50,
				},
			},
			{
				key: 'Description',
				type: FORMLY_FIELD_TYPES.input,
				props: {
					label: 'Description',
					required: true,
					maxLength: 50,
				},
			},
			{
				key: 'UserID',
				type: FORMLY_FIELD_TYPES.input,
				props: {
					label: 'SQL Login User ID',
					required: true,
					maxLength: 50,
				},
			},
			{
				key: 'Password',
				type: FORMLY_FIELD_TYPES.input,
				props: {
					label: 'Password',
					required: true,
					maxLength: 50,
				},
			},
		];
	}

	displayConnectionStatus(result) {
		const message = result.isValid ? `Connection to ${this.model.ServerName} was successful` : `Connection Failed: ${result.errors}`;

		if (result.isValid) {
			this.feedbackService.customSuccess(message);
		} else {
			this.feedbackService.error(message);
		}
	}

	protected refresh(): void {
		throw new Error('Method not implemented.');
	}
}
